import type {
  PageInfo,
  SortDirection,
} from "@alaffia-technology-solutions/client-sdk";

import type {
  GetNextPageParamFunction,
  GetPreviousPageParamFunction,
} from "@tanstack/react-query";

interface Connection {
  pageInfo: PageInfo;
}

type Direction = "forward" | "backward";

interface Ordering {
  field: string;
  direction: SortDirection;
}

interface PageParam {
  cursor: string | null | undefined;
  direction: Direction;
}

interface CreateUseInfiniteQueryPaginationOptions<TPageData> {
  pageSize: number;
  orderBy?: Ordering[];
  getConnection?: (page: TPageData) => Connection | undefined;
  initialPageParam?: PageParam;
}

export const createUseInfiniteQueryPagination = <TPageData = Connection>({
  pageSize,
  orderBy,
  getConnection = (pageData) => pageData as Connection | undefined,
  initialPageParam = {
    cursor: "",
    direction: "forward",
  },
}: CreateUseInfiniteQueryPaginationOptions<TPageData>) => {
  const getNextPageParam: GetNextPageParamFunction<PageParam, TPageData> = (
    lastPage,
  ) => {
    const connection = getConnection(lastPage);
    if (connection?.pageInfo.hasNextPage) {
      return {
        cursor: connection.pageInfo.endCursor,
        direction: "forward",
      };
    }
    return undefined;
  };

  const getPreviousPageParam: GetPreviousPageParamFunction<
    PageParam,
    TPageData
  > = (firstPage) => {
    const connection = getConnection(firstPage);
    if (connection?.pageInfo.hasPreviousPage) {
      return {
        cursor: connection.pageInfo.startCursor,
        direction: "backward",
      };
    }
    return undefined;
  };

  const internalInputFn = (pageParam: PageParam | null) => {
    const cursor = pageParam?.cursor;

    const getArgs = () => {
      if (!cursor) {
        return {
          ...(initialPageParam.direction === "forward"
            ? { first: pageSize }
            : { last: pageSize }),
          orderBy,
        };
      }

      if (pageParam?.direction === "forward") {
        return {
          after: cursor,
          first: pageSize,
          orderBy,
        };
      }

      return {
        before: cursor,
        last: pageSize,
        orderBy,
      };
    };

    const args = [getArgs()];

    return args;
  };

  return {
    getNextPageParam,
    getPreviousPageParam,
    initialPageParam,
    internalInputFn,
  };
};
